import { useQuery } from '@tanstack/react-query'
import {
  Button,
  Card,
  Collapse,
  Descriptions,
  Flex,
  Space,
  Typography
} from 'antd'
import type { CollapseProps } from 'antd'
import { ChevronLeft } from 'lucide-react'
import type { CSSProperties, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'

import {
  organizationsAuditLogsRetrieveOptions,
  type AuditLogDetail
} from 'src/api/main'
import {
  AuditActionTag,
  DateTimeCell,
  Loading,
  MonoValue,
  QueryErrorAlert,
  UserCell
} from 'src/components'
import { WorkspacePageShell } from 'src/features/workspaces/pages/WorkspacePageShell'
import { paths } from 'src/router'
import {
  getAuditEventCode,
  toAuditAcceptLanguage,
  toDisplayString,
  translateAuditAction
} from 'src/utils'

import { useStyles } from './styles'

const { Paragraph, Text } = Typography
const EMPTY_VALUE = '\u2014'

const DESCRIPTION_LABEL_STYLE: CSSProperties = {
  alignItems: 'center',
  width: 116
}
const DESCRIPTION_CONTENT_STYLE: CSSProperties = { alignItems: 'center' }

type AuditDetailLocationState = {
  auditListPath?: string
}

// The detail endpoint returns the generated `AuditLogDetail` plus `changes` /
// `metadata`, which the schema still omits.
type AuditLogDetailWithExtras = AuditLogDetail & {
  changes?: Record<string, unknown> | null
  metadata?: Record<string, unknown> | null
}

const renderText = (value: unknown): ReactNode => {
  const text = toDisplayString(value)

  return text || <Text type="secondary">{EMPTY_VALUE}</Text>
}

const renderMono = (value: unknown): ReactNode => {
  const text = toDisplayString(value)

  return text ? (
    <MonoValue>{text}</MonoValue>
  ) : (
    <Text type="secondary">{EMPTY_VALUE}</Text>
  )
}

const JsonBlock = ({ value }: { value: unknown }) => {
  const { styles } = useStyles()

  return (
    <pre className={styles.codeBlock}>{JSON.stringify(value, null, 2)}</pre>
  )
}

const getLocalizedDescription = (
  record: AuditLogDetailWithExtras,
  language: string,
  fallback: string
) => {
  const description = record.description?.trim()
  if (description) {
    return description
  }

  const descriptions: NonNullable<AuditLogDetailWithExtras['descriptions']> =
    record.descriptions ?? {}
  const baseLanguage = toAuditAcceptLanguage(language)

  return (
    descriptions[baseLanguage]?.trim() ||
    Object.values(descriptions).find((value) => value?.trim()) ||
    fallback
  )
}

export const WorkspaceAppAuditLogDetailPage = () => {
  const { styles } = useStyles()
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const {
    id = '',
    appId = '',
    auditLogId = ''
  } = useParams<{ id: string; appId: string; auditLogId: string }>()
  const workspaceId = Number(id)
  const hasApplicationContext = Boolean(appId)
  const applicationId = hasApplicationContext ? Number(appId) : null
  const language = i18n.resolvedLanguage ?? i18n.language
  const valid =
    Number.isFinite(workspaceId) &&
    workspaceId > 0 &&
    (!hasApplicationContext ||
      (applicationId !== null &&
        Number.isFinite(applicationId) &&
        applicationId > 0)) &&
    Boolean(auditLogId)
  const defaultBackPath = hasApplicationContext
    ? paths.workspaces.apps.audit(id, appId)
    : paths.workspaces.audit(id)
  const state = location.state as AuditDetailLocationState | null
  const backPath = state?.auditListPath?.startsWith('/')
    ? state.auditListPath
    : defaultBackPath

  const { data, isLoading, isError, refetch } = useQuery({
    ...organizationsAuditLogsRetrieveOptions({
      path: { organization_id: workspaceId, id: auditLogId },
      headers: { 'Accept-Language': toAuditAcceptLanguage(language) }
    }),
    enabled: valid
  })
  const record = data as AuditLogDetailWithExtras | undefined

  const backButton = (
    <Button
      type="link"
      icon={<ChevronLeft size={16} />}
      className={styles.backButton}
      onClick={() => navigate(backPath)}
    >
      {t('workspace.apps.auditDetail.backToAudit')}
    </Button>
  )

  if (!valid) {
    return (
      <Navigate
        to={
          hasApplicationContext
            ? paths.workspaces.apps.root(id)
            : paths.workspaces.audit(id)
        }
        replace
      />
    )
  }

  if (isLoading) {
    return (
      <WorkspacePageShell
        breadcrumbLeaf={t('workspace.apps.auditDetail.breadcrumb')}
      >
        {backButton}
        <Loading />
      </WorkspacePageShell>
    )
  }

  if (isError || !record) {
    return (
      <WorkspacePageShell
        breadcrumbLeaf={t('workspace.apps.auditDetail.breadcrumb')}
      >
        <Flex vertical gap={14}>
          {backButton}
          <QueryErrorAlert
            messageText={t('workspace.apps.auditDetail.loadError')}
            onRetry={() => void refetch()}
          />
        </Flex>
      </WorkspacePageShell>
    )
  }

  if (
    (record.organization_id != null &&
      record.organization_id !== workspaceId) ||
    (hasApplicationContext && record.application_id !== applicationId)
  ) {
    return <Navigate to={backPath} replace />
  }

  const eventName = translateAuditAction(t, getAuditEventCode(record))
  const actorName = toDisplayString(record.actor?.full_name)
  const actorEmail = toDisplayString(record.actor?.email)
  const actorFallback =
    actorName || actorEmail || toDisplayString(record.actor?.id)
  const description = getLocalizedDescription(record, language, eventName)
  const additionalItems: CollapseProps['items'] = [
    {
      key: 'changes',
      label: t('workspace.apps.auditDetail.sections.changes'),
      children: <JsonBlock value={record.changes ?? {}} />
    },
    {
      key: 'metadata',
      label: t('workspace.apps.auditDetail.sections.metadata'),
      children: <JsonBlock value={record.metadata ?? {}} />
    },
    {
      key: 'descriptions',
      label: t('workspace.apps.auditDetail.sections.localizedDescriptions'),
      children: <JsonBlock value={record.descriptions ?? {}} />
    }
  ]

  return (
    <WorkspacePageShell
      breadcrumbLeaf={t('workspace.apps.auditDetail.breadcrumb')}
    >
      <Flex vertical gap={14}>
        {backButton}

        <Card
          size="small"
          variant="outlined"
          title={
            <Space size={8} wrap>
              <Text strong>{eventName}</Text>
              <AuditActionTag action={record.action} maxWidth={160} />
            </Space>
          }
          extra={<DateTimeCell value={record.timestamp} variant="dateTime" />}
        >
          <Descriptions
            column={{ xs: 1, md: 2 }}
            size="small"
            colon={false}
            styles={{
              label: DESCRIPTION_LABEL_STYLE,
              content: DESCRIPTION_CONTENT_STYLE
            }}
          >
            <Descriptions.Item
              span={2}
              label={t('workspace.apps.auditDetail.fields.description')}
            >
              {description ? (
                <Paragraph style={{ marginBottom: 0 }}>{description}</Paragraph>
              ) : (
                <Text type="secondary">{EMPTY_VALUE}</Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item
              label={t('workspace.apps.auditDetail.fields.eventId')}
            >
              {renderMono(record.id)}
            </Descriptions.Item>
            <Descriptions.Item
              label={t('workspace.apps.auditDetail.fields.eventType')}
            >
              {renderText(record.event_type)}
            </Descriptions.Item>
            <Descriptions.Item
              label={t('workspace.apps.auditDetail.fields.channel')}
            >
              {renderText(record.channel)}
            </Descriptions.Item>
            <Descriptions.Item
              label={t('workspace.apps.auditDetail.fields.entityType')}
            >
              {renderText(record.entity_type)}
            </Descriptions.Item>
            <Descriptions.Item
              label={t('workspace.apps.auditDetail.fields.entityId')}
            >
              {renderMono(record.entity_id)}
            </Descriptions.Item>
            <Descriptions.Item
              label={t('workspace.apps.auditDetail.fields.category')}
            >
              {renderText(record.category)}
            </Descriptions.Item>
            <Descriptions.Item
              label={t('workspace.apps.auditDetail.fields.application')}
            >
              {renderText(record.application_name)}
            </Descriptions.Item>
            <Descriptions.Item
              label={t('workspace.apps.auditDetail.fields.applicationId')}
            >
              {renderMono(record.application_id)}
            </Descriptions.Item>
            <Descriptions.Item
              label={t('workspace.apps.auditDetail.fields.organizationId')}
            >
              {renderMono(record.organization_id)}
            </Descriptions.Item>
            <Descriptions.Item
              label={t('workspace.apps.auditDetail.fields.service')}
            >
              {renderText(record.service)}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        <Card
          size="small"
          variant="outlined"
          title={t('workspace.apps.auditDetail.sections.actorAndRequest')}
        >
          <Descriptions
            column={{ xs: 1, md: 2 }}
            size="small"
            colon={false}
            styles={{
              label: DESCRIPTION_LABEL_STYLE,
              content: DESCRIPTION_CONTENT_STYLE
            }}
          >
            <Descriptions.Item
              label={t('workspace.apps.auditDetail.fields.actor')}
            >
              {actorFallback ? (
                <UserCell
                  firstName={actorFallback}
                  email={actorName ? actorEmail : undefined}
                  size="sm"
                />
              ) : (
                <Text type="secondary">{EMPTY_VALUE}</Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item
              label={t('workspace.apps.auditDetail.fields.actorId')}
            >
              {renderMono(record.actor?.id)}
            </Descriptions.Item>
            <Descriptions.Item
              label={t('workspace.apps.auditDetail.fields.method')}
            >
              {renderText(record.method)}
            </Descriptions.Item>
            <Descriptions.Item
              label={t('workspace.apps.auditDetail.fields.clientIp')}
            >
              {renderMono(record.client_ip)}
            </Descriptions.Item>
            <Descriptions.Item
              span={2}
              label={t('workspace.apps.auditDetail.fields.url')}
            >
              {record.url ? (
                <MonoValue ellipsis>{record.url}</MonoValue>
              ) : (
                <Text type="secondary">{EMPTY_VALUE}</Text>
              )}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        <Card
          size="small"
          variant="outlined"
          title={t('workspace.apps.auditDetail.sections.additionalData')}
          styles={{ body: { padding: 0 } }}
        >
          <Collapse ghost items={additionalItems} />
        </Card>
      </Flex>
    </WorkspacePageShell>
  )
}
